import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Stack,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  KeyboardArrowUp as KeyboardArrowUpIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Delete as DeleteIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import codexTypeService from '../services/codexTypeService';
import processService from '../services/processService';

// Attribute data types
const ATTRIBUTE_DATA_TYPES = [
  { value: 'STRING', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'JSON', label: 'JSON' }
];

const NewCodexType = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableProcesses, setAvailableProcesses] = useState([]);
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    attributes: []
  });

  // Temporary state for new attribute
  const [newAttribute, setNewAttribute] = useState({
    code: '',
    description: '',
    dataType: 'STRING',
    isRequired: false,
    schema: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch available processes
        const processes = await processService.getAll();
        setAvailableProcesses(processes);

        // If in edit mode, fetch codex type data
        if (isEdit) {
          const codexType = await codexTypeService.getById(id);
          setFormData({
            code: codexType.code || '',
            description: codexType.description || '',
            attributes: codexType.attributes || []
          });
          setSelectedProcesses(codexType.codexTypeProcesses || []);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle new attribute field change
  const handleNewAttributeChange = (e) => {
    const { name, value, checked } = e.target;
    setNewAttribute(prev => ({
      ...prev,
      [name]: name === 'isRequired' ? checked : value
    }));
  };

  // Handle schema change
  const handleSchemaChange = (e) => {
    try {
      const schemaValue = e.target.value;
      const parsedSchema = schemaValue ? JSON.parse(schemaValue) : null;
      setNewAttribute(prev => ({
        ...prev,
        schema: parsedSchema
      }));
    } catch (error) {
      console.error('Invalid JSON schema:', error);
      // Don't update the schema if it's invalid
    }
  };

  // Add attribute to document type form
  const handleAddAttribute = () => {
    // Validation
    if (!newAttribute.code || !newAttribute.description) {
      setError('The attribute code and description are required');
      return;
    }
    
    // Add the new attribute
    const newField = {
      ...newAttribute,
      codexTypeId: formData.id
    };
    
    setFormData(prev => ({
      ...prev,
      attributes: [...prev.attributes, newField]
    }));
    
    // Reset the field
    setNewAttribute({
      code: '',
      description: '',
      dataType: 'STRING',
      isRequired: false,
      schema: null
    });
  };

  // Remove attribute from document type form
  const handleRemoveAttribute = (index) => {
    setFormData(prev => ({
      ...prev,
      attributes: prev.attributes.filter((_, i) => i !== index)
    }));
  };

  // Handle process selection
  const handleProcessSelection = (processId) => {
    setSelectedProcesses(prev => {
      const isSelected = prev.some(p => p.processId === processId);
      if (isSelected) {
        // Remove the process and reorder the remaining ones
        return prev
          .filter(p => p.processId !== processId)
          .map((p, index) => ({
            ...p,
            processExecutionLevel: index + 1
          }));
      } else {
        const process = availableProcesses.find(p => p.id === processId);
        // Add new process with the next available execution level
        const maxLevel = prev.length > 0 ? Math.max(...prev.map(p => p.processExecutionLevel)) : 0;
        return [...prev, {
          processId: process.id,
          processExecutionLevel: maxLevel + 1
        }];
      }
    });
  };

  // Handle process reordering
  const handleProcessReorder = (processId, newLevel) => {
    setSelectedProcesses(prev => {
      const updatedProcesses = [...prev];
      const currentIndex = updatedProcesses.findIndex(p => p.processId === processId);
      const currentProcess = updatedProcesses[currentIndex];
      
      // Remove the process from its current position
      updatedProcesses.splice(currentIndex, 1);
      
      // Insert it at the new position
      updatedProcesses.splice(newLevel - 1, 0, {
        ...currentProcess,
        processExecutionLevel: newLevel
      });
      
      // Update execution levels for all processes
      return updatedProcesses.map((p, index) => ({
        ...p,
        processExecutionLevel: index + 1
      }));
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const codexTypeData = {
        ...formData,
        codexTypeProcesses: selectedProcesses
      };

      if (isEdit) {
        await codexTypeService.update(id, codexTypeData);
      } else {
        await codexTypeService.create(codexTypeData);
      }

      navigate('/codex-types');
    } catch (err) {
      console.error('Error saving codex type:', err);
      setError('Failed to save codex type. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !formData.code) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          {isEdit ? 'Modifica Tipo Codex' : 'Nuovo Tipo Codex'}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/codex-types')}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.code}
          >
            {loading ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <TextField
              fullWidth
              label="Codice"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              disabled={isEdit}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Descrizione"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={2}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Attributi
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {formData.attributes.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                      <TableCell>Codice</TableCell>
                      <TableCell>Descrizione</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Obbligatorio</TableCell>
                      <TableCell>Dettagli</TableCell>
                      <TableCell align="right">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.attributes.map((attr, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{attr.code}</TableCell>
                        <TableCell>{attr.description}</TableCell>
                        <TableCell>{attr.dataType}</TableCell>
                        <TableCell>
                          {attr.isRequired ? 
                            <Chip size="small" label="SI" color="error" /> : 
                            <Chip size="small" label="NO" color="success" />
                          }
                        </TableCell>
                        <TableCell>
                          {attr.dataType === 'JSON' && attr.schema
                            ? <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CodeIcon />}
                              >
                                Schema
                              </Button>
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            size="small" 
                            color="error" 
                            onClick={() => handleRemoveAttribute(index)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Nessun attributo definito.
              </Typography>
            )}
            
            <Box sx={{ p: 2, border: '1px dashed #ddd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Aggiungi Nuovo Attributo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Codice Attributo"
                    name="code"
                    value={newAttribute.code}
                    onChange={handleNewAttributeChange}
                    helperText="Usato come chiave nei metadati (camelCase, no spazi)"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required
                    label="Descrizione"
                    name="description"
                    value={newAttribute.description}
                    onChange={handleNewAttributeChange}
                    helperText="Descrizione user-friendly"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required size="small">
                    <InputLabel>Tipo Dati</InputLabel>
                    <Select
                      name="dataType"
                      value={newAttribute.dataType}
                      label="Tipo Dati"
                      onChange={handleNewAttributeChange}
                    >
                      {ATTRIBUTE_DATA_TYPES.map(type => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newAttribute.isRequired}
                        onChange={handleNewAttributeChange}
                        name="isRequired"
                        color="primary"
                        size="small"
                      />
                    }
                    label="Campo Obbligatorio"
                  />
                </Grid>
                
                {newAttribute.dataType === 'JSON' && (
                  <Grid item xs={12}>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Definizione Schema JSON
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Inserisci uno schema JSON valido che definisce la struttura di questo attributo JSON.
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      label="Schema JSON"
                      name="schema"
                      onChange={handleSchemaChange}
                      placeholder={`{
  "$schema": "http://json-schema.org/draft-07/schema#",
  "type": "object",
  "properties": {
    "property1": {
      "type": "string",
      "description": "Descrizione di property1"
    },
    "property2": {
      "type": "number",
      "description": "Descrizione di property2"
    }
  },
  "required": ["property1"]
}`}
                      helperText="Inserisci uno schema JSON valido per definire la struttura di questo attributo"
                      size="small"
                      variant="outlined"
                      InputProps={{
                        sx: { fontFamily: 'monospace' }
                      }}
                    />
                  </Grid>
                )}
                
                <Grid item xs={12}>
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={handleAddAttribute}
                    disabled={!newAttribute.code || !newAttribute.description}
                  >
                    Aggiungi Attributo
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Processi
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Seleziona i processi che possono essere eseguiti per questo tipo di codex e imposta il loro ordine di esecuzione:
              </Typography>
              
              <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableProcesses.map((process) => (
                  <Chip
                    key={process.id}
                    label={process.code}
                    onClick={() => handleProcessSelection(process.id)}
                    color={selectedProcesses.some(p => p.processId === process.id) ? "primary" : "default"}
                    variant={selectedProcesses.some(p => p.processId === process.id) ? "filled" : "outlined"}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
              
              {selectedProcesses.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Processi selezionati (ordine di esecuzione):
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                          <TableCell width="80px">Ordine</TableCell>
                          <TableCell>Codice</TableCell>
                          <TableCell>Descrizione</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell align="right">Azioni</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedProcesses
                          .sort((a, b) => a.processExecutionLevel - b.processExecutionLevel)
                          .map((processData) => {
                            const process = availableProcesses.find(p => p.id === processData.processId);
                            return (
                              <TableRow 
                                key={processData.processId}
                                sx={{ 
                                  cursor: 'move',
                                  '&:hover': { bgcolor: 'action.hover' }
                                }}
                              >
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <IconButton
                                      size="small"
                                      disabled={processData.processExecutionLevel === 1}
                                      onClick={() => handleProcessReorder(processData.processId, processData.processExecutionLevel - 1)}
                                    >
                                      <KeyboardArrowUpIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2">
                                      {processData.processExecutionLevel}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      disabled={processData.processExecutionLevel === selectedProcesses.length}
                                      onClick={() => handleProcessReorder(processData.processId, processData.processExecutionLevel + 1)}
                                    >
                                      <KeyboardArrowDownIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                <TableCell>{process?.code}</TableCell>
                                <TableCell>{process?.description}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={process?.processType?.id || '-'} 
                                    size="small" 
                                    color="primary"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleProcessSelection(processData.processId)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default NewCodexType; 